import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

interface MultiSelectProps {
  options: (string | null | undefined)[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  maxDisplayItems?: number;
}

export default function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options...",
  className = "",
  maxDisplayItems = 2
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter out null/undefined values and then filter by search term
  const validOptions = options.filter((option): option is string => option !== null && option !== undefined);
  const filteredOptions = validOptions.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const handleRemoveOption = (option: string) => {
    onChange(selected.filter(item => item !== option));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected items display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-[42px] px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white cursor-pointer flex items-center justify-between hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-1 flex-1">
          {selected.length === 0 ? (
            <span className="text-zinc-500 dark:text-zinc-400">{placeholder}</span>
          ) : selected.length <= maxDisplayItems ? (
            selected.map((item) => (
              <span
                key={item}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
              >
                {item}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveOption(item);
                  }}
                  className="ml-1 hover:bg-emerald-500/20 rounded-full p-0.5"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-zinc-700 dark:text-zinc-200">
              {selected.slice(0, maxDisplayItems).join(', ')} 
              <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                +{selected.length - maxDisplayItems} more
              </span>
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              title="Clear all"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
          <ChevronDownIcon 
            className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-30 w-full mt-1 rounded-lg border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900 max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-zinc-200 dark:border-zinc-800">
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => handleToggleOption(option)}
                  className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                >
                  <div className="flex items-center justify-center w-4 h-4 mr-3 border border-zinc-300 dark:border-zinc-600 rounded">
                    {selected.includes(option) && (
                      <CheckIcon className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                    )}
                  </div>
                  <span className="text-zinc-700 dark:text-zinc-200 flex-1">{option}</span>
                  {selected.includes(option) && (
                    <div className="w-2 h-2 bg-emerald-600 dark:bg-emerald-400 rounded-full ml-2"></div>
                  )}
                </div>
              ))
            )}
          </div>
          
          {/* Footer with actions */}
          {selected.length > 0 && (
            <div className="p-2 border-t border-zinc-200 dark:border-zinc-800 flex justify-between items-center bg-zinc-50 dark:bg-zinc-800/60">
              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                {selected.length} selected
              </span>
              <button
                onClick={handleClearAll}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}